import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  Alert,
  Box,
  Button,
  Card,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  LinearProgress,
  MenuItem,
  Paper,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "../../../auth/useAuth";
import { can } from "../../../auth/can";
import RollbackModal from "./RollbackModal";
import DispatchOperationsPanel from "./DispatchOperationsPanel";

import { APP_API_V1_BASE } from "../../../config/api";
const API_BASE = `${APP_API_V1_BASE}/firmware/deployments`;
const DEVICE_API_BASE = `${APP_API_V1_BASE}/devices`;

const formatDateTime = (value) => {
  if (!value) return "—";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return "—";
  return parsed.toLocaleString();
};

const getDeploymentStatusLabel = (deployment) => {
  const status = String(deployment?.status || "").toUpperCase();
  const scheduledAt = deployment?.scheduled_at
    ? new Date(deployment.scheduled_at)
    : null;

  if (
    status === "PENDING" &&
    deployment?.rollout_strategy === "SCHEDULED" &&
    scheduledAt &&
    !Number.isNaN(scheduledAt.getTime()) &&
    scheduledAt.getTime() > Date.now()
  ) {
    return "Scheduled";
  }

  if (status === "IN_PROGRESS") return "Dispatched";
  if (status === "PENDING") return "Pending Ack";
  if (status === "COMPLETED") return "Completed";
  if (status === "FAILED") return "Failed";
  if (status === "CANCELED") return "Canceled";
  return status || "-";
};

const getFailureReasonLabel = (message) => {
  const value = String(message || "").toLowerCase();
  if (
    value.includes("timed out waiting for initial ota acknowledgement") ||
    value.includes("pending ack timeout")
  ) {
    return "Timed out waiting for device acknowledgement";
  }
  return message || "Unknown failure";
};

const SnapshotField = ({ label, value, mono = false }) => (
  <Box
    sx={{
      border: "1px solid #E2E8F0",
      borderRadius: "10px",
      p: 1.2,
      backgroundColor: "#FFFFFF",
      minHeight: 72,
    }}
  >
    <Typography fontSize={11} color="#64748B" fontWeight={700}>
      {label}
    </Typography>
    <Typography
      fontSize={13}
      fontWeight={700}
      color="#0F172A"
      sx={{
        mt: 0.3,
        wordBreak: "break-all",
        fontFamily: mono ? "monospace" : "inherit",
      }}
    >
      {value || "--"}
    </Typography>
  </Box>
);

export default function DeploymentDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const token = localStorage.getItem("authToken");
  const { activeOrg, permissions } = useAuth();

  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState("");
  const [detail, setDetail] = useState(null);
  const [devices, setDevices] = useState([]);
  const [statusFilter, setStatusFilter] = useState("");
  const [search, setSearch] = useState("");
  const [selectedDeviceId, setSelectedDeviceId] = useState("");
  const [historyRows, setHistoryRows] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [attemptRows, setAttemptRows] = useState([]);
  const [attemptsLoading, setAttemptsLoading] = useState(false);
  const [dispatchRows, setDispatchRows] = useState([]);
  const [dispatchLoading, setDispatchLoading] = useState(false);
  const [redeployWarningOpen, setRedeployWarningOpen] = useState(false);
  const [openRollback, setOpenRollback] = useState(false);
  const [redeploying, setRedeploying] = useState(false);
  const [liveMode, setLiveMode] = useState(true);
  const [lastRefreshAt, setLastRefreshAt] = useState(null);

  const headers = useMemo(
    () => ({
      Authorization: `Bearer ${token}`,
      ...(activeOrg?.id && { "X-Org-ID": activeOrg.id }),
    }),
    [token, activeOrg?.id],
  );

  const fetchAll = useCallback(
    async (silent = false) => {
      if (!silent) {
        setLoading(true);
        setDispatchLoading(true);
      }
      setError("");
      try {
        const [detailRes, devicesRes, dispatchRes] = await Promise.all([
          axios.get(`${API_BASE}/${id}`, { headers }),
          axios.get(`${API_BASE}/${id}/devices`, {
            headers,
            params: {
              ...(statusFilter ? { status: statusFilter } : {}),
              ...(search ? { search } : {}),
            },
          }),
          axios.get(`${API_BASE}/${id}/dispatch`, {
            headers,
            params: {
              limit: 200,
              offset: 0,
              ...(search ? { search } : {}),
            },
          }),
        ]);
        setDetail(detailRes.data);
        setDevices(devicesRes.data.devices || []);
        setDispatchRows(dispatchRes.data?.dispatch_rows || []);
        setLastRefreshAt(new Date());
      } catch (e) {
        setError(
          e?.response?.data?.error || "Failed to load deployment details.",
        );
        setDispatchRows([]);
      } finally {
        setLoading(false);
        setDispatchLoading(false);
      }
    },
    [id, headers, statusFilter, search],
  );

  const fetchHistory = useCallback(
    async (deviceId) => {
      if (!deviceId) {
        setHistoryRows([]);
        return;
      }
      setHistoryLoading(true);
      try {
        const res = await axios.get(
          `${DEVICE_API_BASE}/${deviceId}/firmware/history`,
          {
            headers,
          },
        );
        setHistoryRows(res.data.history || []);
      } catch {
        setHistoryRows([]);
      } finally {
        setHistoryLoading(false);
      }
    },
    [headers],
  );

  const fetchAttempts = useCallback(async () => {
    setAttemptsLoading(true);
    try {
      const res = await axios.get(`${API_BASE}/${id}/attempts`, { headers });
      setAttemptRows(res.data.attempts || []);
    } catch {
      setAttemptRows([]);
    } finally {
      setAttemptsLoading(false);
    }
  }, [headers, id]);

  useEffect(() => {
    fetchAll();
    fetchAttempts();
  }, [fetchAll, fetchAttempts]);

  useEffect(() => {
    const activeStatuses = new Set(["PENDING", "IN_PROGRESS"]);
    const isActive = activeStatuses.has((detail?.status || "").toUpperCase());
    if (!liveMode || !isActive) return undefined;

    const timer = setInterval(() => {
      fetchAll(true);
    }, 4000);
    return () => clearInterval(timer);
  }, [fetchAll, liveMode, detail?.status]);

  useEffect(() => {
    if (devices.length === 0) {
      setSelectedDeviceId("");
      setHistoryRows([]);
      return;
    }

    if (!selectedDeviceId) {
      const first = devices[0].device_id;
      setSelectedDeviceId(first);
      fetchHistory(first);
    }
  }, [devices, selectedDeviceId, fetchHistory]);

  const onCancel = async () => {
    setRefreshing(true);
    setError("");
    try {
      await axios.post(`${API_BASE}/${id}/cancel`, {}, { headers });
      await fetchAll(true);
    } catch (e) {
      setError(e?.response?.data?.error || "Failed to cancel deployment.");
    } finally {
      setRefreshing(false);
    }
  };

  const onRedeploy = async () => {
    if (!id) return;
    setRedeploying(true);
    setError("");
    try {
      const res = await axios.post(`${API_BASE}/${id}/redeploy`, {}, { headers });
      const newDeploymentId = res?.data?.deployment?.id;
      if (newDeploymentId) {
        navigate(`/ota/deployments/${newDeploymentId}`);
        return;
      }
      await fetchAll(true);
    } catch (e) {
      setError(e?.response?.data?.error || "Failed to redeploy firmware.");
    } finally {
      setRedeploying(false);
    }
  };

  const hasActiveAttempt = attemptRows.some((row) => {
    const status = String(row?.status || "").toUpperCase();
    return status === "PENDING" || status === "IN_PROGRESS";
  });

  if (loading && !detail) {
    return (
      <Paper sx={{ mt: { xs: 5, md: 0 }, p: 3, borderRadius: "18px" }}>
        <Typography>Loading deployment details...</Typography>
      </Paper>
    );
  }

  if (!detail) {
    return (
      <Paper sx={{ mt: { xs: 5, md: 0 }, p: 3, borderRadius: "18px" }}>
        <Alert severity="error">{error || "Deployment not found."}</Alert>
      </Paper>
    );
  }

  const s = detail.summary || {};
  const dispatch = detail.dispatch || {};
  const successRate = Number(s.success_rate || 0);
  const deploymentStatus = String(detail.status || "").toUpperCase();
  const attemptNumber = Number(detail.attempt_number || attemptRows.length || 1);
  const parentDeploymentId = detail.parent_deployment_id || null;
  const deploymentStatusLabel = getDeploymentStatusLabel(detail);
  const canManageDispatch = can(permissions, "firmware.deploy");
  const statusColor =
    deploymentStatus === "COMPLETED"
      ? "success"
      : deploymentStatus === "FAILED"
        ? "error"
        : deploymentStatus === "CANCELED"
          ? "default"
          : deploymentStatus === "IN_PROGRESS"
            ? "info"
            : "warning";

  const dispatchByDevice = dispatchRows.reduce((map, row) => {
    const key = String(row?.device_id || "");
    if (!key) return map;
    map.set(key, row);
    return map;
  }, new Map());

  const metrics = [
    { label: "Total", value: s.total_devices || 0 },
    { label: "Pending", value: s.pending || 0 },
    { label: "Downloading", value: s.downloading || 0 },
    { label: "Installing", value: s.installing || 0 },
    { label: "Installed", value: s.installed || 0 },
    { label: "Failed", value: s.failed || 0 },
    { label: "Canceled", value: s.canceled || 0 },
  ];

  const snapshotFields = [
    {
      label: "Deployment ID",
      value: detail.deployment_id || id,
      mono: true,
    },
    {
      label: "Firmware ID",
      value: detail.firmware_id || detail?.firmware?.id,
      mono: true,
    },
    {
      label: "Target Scope",
      value: `${detail.target_type || "--"} - ${detail.target_id || "--"}`,
      mono: true,
    },
    {
      label: "Firmware",
      value: `${detail?.firmware?.name || "Firmware"} - v${detail?.firmware?.version || "--"}`,
    },
    {
      label: "Rollout",
      value: detail.rollout_strategy || "--",
    },
    {
      label: "Scheduled For",
      value: detail.scheduled_at ? formatDateTime(detail.scheduled_at) : "Immediate",
    },
    {
      label: "Created At",
      value: detail.created_at ? formatDateTime(detail.created_at) : "--",
    },
    {
      label: "Last Refreshed",
      value: lastRefreshAt ? formatDateTime(lastRefreshAt) : "--",
    },
  ];

  const dispatchFields = [
    { label: "Queue Total", value: dispatch.total || 0 },
    { label: "Pending", value: dispatch.pending || 0 },
    { label: "Processing", value: dispatch.processing || 0 },
    { label: "Dispatched", value: dispatch.dispatched || 0 },
    { label: "Dead Letter", value: dispatch.dead_letter || 0 },
    { label: "Canceled", value: dispatch.canceled || 0 },
  ];

  return (
    <Box sx={{ mt: { xs: 5, md: 0 } }}>
      <Paper
        sx={{
          p: 3,
          borderRadius: "20px",
          border: "1px solid #E4EAF4",
          boxShadow: "0 8px 24px rgba(13, 35, 68, 0.08)",
        }}
      >
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            gap: 2,
            flexWrap: "wrap",
            mb: 2.2,
          }}
        >
          <Box>
            <Typography fontWeight={800} fontSize={18}>
              Deployment Detail
            </Typography>
            <Typography fontSize={12} color="#4C6796" fontWeight={700}>
              {attemptNumber > 1
                ? `Redeploy attempt #${attemptNumber}`
                : "Original deployment"}
            </Typography>
            <Typography fontSize={12} color="#6A7C9E">
              {detail?.firmware?.name || "Firmware"} · v
              {detail?.firmware?.version || "—"}
            </Typography>
            {parentDeploymentId && (
              <Typography fontSize={12} color="#6A7C9E" sx={{ mt: 0.5 }}>
                Retry of previous deployment{" "}
                <Typography
                  component="span"
                  fontSize={12}
                  fontWeight={800}
                  color="#1E4FBA"
                  sx={{ cursor: "pointer" }}
                  onClick={() => navigate(`/ota/deployments/${parentDeploymentId}`)}
                >
                  {parentDeploymentId}
                </Typography>
              </Typography>
            )}
            <Box display="flex" alignItems="center" gap={1} mt={0.7}>
              <Typography fontSize={11} color="#6E7D96" fontWeight={700}>
                Live Refresh
              </Typography>
              <Switch
                size="small"
                checked={liveMode}
                onChange={(e) => setLiveMode(e.target.checked)}
              />
              <Typography fontSize={11} color="#6E7D96">
                {lastRefreshAt ? `Updated ${formatDateTime(lastRefreshAt)}` : "Not updated yet"}
              </Typography>
            </Box>
          </Box>
          <Box display="flex" gap={1} alignItems="center" flexWrap="wrap">
            <Chip label={deploymentStatusLabel} color={statusColor} />
            <Chip
              label={detail.rollout_strategy}
              variant="outlined"
              sx={{ borderColor: "#BFD0EA", color: "#294D86" }}
            />
            <Button
              variant="outlined"
              onClick={() => navigate(`/ota/deployments/${id}/progress`)}
              sx={{ textTransform: "none", borderRadius: "10px" }}
            >
              Progress View
            </Button>
            <Button
              variant="outlined"
              onClick={() => fetchAll()}
              disabled={loading || dispatchLoading}
              sx={{ textTransform: "none", borderRadius: "10px" }}
            >
              {loading || dispatchLoading ? "Refreshing..." : "Refresh"}
            </Button>
            {can(permissions, "firmware.deploy") &&
              detail?.actions?.can_cancel && (
                <Button
                  variant="outlined"
                  color="error"
                  onClick={onCancel}
                  disabled={refreshing}
                  sx={{ textTransform: "none", borderRadius: "10px" }}
                >
                  {refreshing ? "Canceling..." : "Cancel Deployment"}
                </Button>
              )}
            {can(permissions, "firmware.deploy") &&
              detail?.actions?.can_rollback && (
                <Button
                  variant="contained"
                  onClick={() => setOpenRollback(true)}
                  sx={{ textTransform: "none", borderRadius: "10px" }}
                >
                  Rollback
                </Button>
              )}
            {can(permissions, "firmware.deploy") &&
              deploymentStatus === "FAILED" && (
                <Button
                  variant="contained"
                  color="secondary"
                  onClick={() => {
                    if (hasActiveAttempt) {
                      setRedeployWarningOpen(true);
                      return;
                    }
                    onRedeploy();
                  }}
                  disabled={redeploying}
                  sx={{ textTransform: "none", borderRadius: "10px" }}
                >
                  {redeploying ? "Redeploying..." : "Redeploy"}
                </Button>
              )}
          </Box>
        </Box>

        <Box sx={{ mb: 2 }}>
          <Typography fontSize={13} fontWeight={800} color="#314A76" mb={0.8}>
            Deployment Snapshot
          </Typography>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "repeat(1, minmax(0, 1fr))",
                sm: "repeat(2, minmax(0, 1fr))",
                lg: "repeat(4, minmax(0, 1fr))",
              },
              gap: 1,
            }}
          >
            {snapshotFields.map((field) => (
              <SnapshotField
                key={field.label}
                label={field.label}
                value={field.value}
                mono={field.mono}
              />
            ))}
          </Box>
        </Box>

        <Box sx={{ mb: 2 }}>
          <Typography fontSize={13} fontWeight={800} color="#314A76" mb={0.8}>
            Dispatch Snapshot
          </Typography>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "repeat(2, minmax(0, 1fr))",
                md: "repeat(3, minmax(0, 1fr))",
                xl: "repeat(6, minmax(0, 1fr))",
              },
              gap: 1,
            }}
          >
            {dispatchFields.map((field) => (
              <SnapshotField
                key={field.label}
                label={field.label}
                value={field.value}
              />
            ))}
          </Box>
        </Box>

        <Card
          sx={{
            p: 2,
            borderRadius: "14px",
            border: "1px solid #DAE6FA",
            background: "#F4F8FF",
            mb: 2,
          }}
        >
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            mb={0.9}
          >
            <Typography fontSize={13} fontWeight={700} color="#4C6796">
              Success Rate
            </Typography>
            <Typography fontSize={30} fontWeight={800} color="#1E4FBA">
              {successRate}%
            </Typography>
          </Box>
          <LinearProgress
            variant="determinate"
            value={Math.max(0, Math.min(100, successRate))}
            sx={{
              height: 10,
              borderRadius: 99,
              backgroundColor: "#D7E4FA",
              "& .MuiLinearProgress-bar": {
                borderRadius: 99,
                background: "linear-gradient(90deg, #2B6FD6, #4784E6)",
              },
            }}
          />
        </Card>

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "repeat(2,1fr)", md: "repeat(7,1fr)" },
            gap: 1,
            mb: 2.2,
          }}
        >
          {metrics.map((m) => (
            <Card
              key={m.label}
              sx={{ p: 1.2, borderRadius: "10px", border: "1px solid #E5ECF7" }}
            >
              <Typography fontSize={11} color="#6E7D96" fontWeight={700}>
                {m.label}
              </Typography>
              <Typography fontSize={22} fontWeight={800} color="#27487F">
                {m.value}
              </Typography>
            </Card>
          ))}
        </Box>

        <Box
          sx={{
            display: "flex",
            gap: 1.2,
            mb: 1.2,
            flexWrap: "wrap",
            alignItems: "center",
          }}
        >
          <TextField
            size="small"
            label="Search device"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{ minWidth: 220 }}
          />
          <TextField
            select
            size="small"
            label="Status"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            sx={{ minWidth: 180 }}
          >
            <MenuItem value="">All</MenuItem>
            <MenuItem value="PENDING">PENDING</MenuItem>
            <MenuItem value="DOWNLOADING">DOWNLOADING</MenuItem>
            <MenuItem value="INSTALLING">INSTALLING</MenuItem>
            <MenuItem value="INSTALLED">INSTALLED</MenuItem>
            <MenuItem value="FAILED">FAILED</MenuItem>
            <MenuItem value="CANCELED">CANCELED</MenuItem>
          </TextField>
          <Button
            variant="contained"
            onClick={() => fetchAll()}
            sx={{ textTransform: "none" }}
          >
            Refresh
          </Button>
        </Box>
        {dispatchLoading && (
          <Typography fontSize={12} color="#64748B" sx={{ mb: 1 }}>
            Refreshing dispatch diagnostics...
          </Typography>
        )}

        <Table size="small">
          <TableHead>
            <TableRow sx={{ background: "#F1F5FC" }}>
              <TableCell sx={{ fontWeight: 800, color: "#42557A" }}>
                Device
              </TableCell>
              <TableCell sx={{ fontWeight: 800, color: "#42557A" }}>
                Status
              </TableCell>
              <TableCell sx={{ fontWeight: 800, color: "#42557A" }}>
                Current
              </TableCell>
              <TableCell sx={{ fontWeight: 800, color: "#42557A" }}>
                Target
              </TableCell>
              <TableCell sx={{ fontWeight: 800, color: "#42557A" }}>
                Last Reported
              </TableCell>
              <TableCell sx={{ fontWeight: 800, color: "#42557A" }}>
                Dispatch Attempts
              </TableCell>
              <TableCell sx={{ fontWeight: 800, color: "#42557A" }}>
                Next Retry
              </TableCell>
              <TableCell sx={{ fontWeight: 800, color: "#42557A" }}>
                Device Error
              </TableCell>
              <TableCell sx={{ fontWeight: 800, color: "#42557A" }}>
                Dispatch Error
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {devices.map((d) => {
              const dispatchRow = dispatchByDevice.get(String(d.device_id || ""));
              return (
              <TableRow
                key={d.device_id}
                hover
                selected={selectedDeviceId === d.device_id}
                onClick={() => {
                  setSelectedDeviceId(d.device_id);
                  fetchHistory(d.device_id);
                }}
                sx={{ cursor: "pointer" }}
              >
                <TableCell>
                  <Typography fontWeight={700} fontSize={13}>
                    {d.device_name || "Unnamed"}
                  </Typography>
                  <Typography
                    fontFamily="monospace"
                    fontSize={11}
                    color="#5A6E91"
                  >
                    {d.device_id}
                  </Typography>
                </TableCell>
                <TableCell>{d.status}</TableCell>
                <TableCell>{d.current_version || "—"}</TableCell>
                <TableCell>{d.target_version || "—"}</TableCell>
                <TableCell>
                  {d.last_reported_at
                    ? formatDateTime(d.last_reported_at)
                    : "—"}
                </TableCell>
                <TableCell>
                  {dispatchRow
                    ? `${dispatchRow.attempt_count || 0} / ${dispatchRow.max_attempts || 0}`
                    : "--"}
                </TableCell>
                <TableCell>
                  {dispatchRow?.next_retry_at
                    ? formatDateTime(dispatchRow.next_retry_at)
                    : "--"}
                </TableCell>
                <TableCell>{getFailureReasonLabel(d.error_message)}</TableCell>
                <TableCell>{dispatchRow?.last_error || "--"}</TableCell>
              </TableRow>
              );
            })}
            {devices.length === 0 && (
              <TableRow>
                <TableCell colSpan={9}>
                  <Typography color="#6E7D96">
                    No device rows found for this filter.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>

        <DispatchOperationsPanel
          deploymentId={id}
          headers={headers}
          canManageDispatch={canManageDispatch}
        />

        <Box mt={2.2}>
          <Typography fontWeight={800} fontSize={15} mb={1}>
            Redeploy History
          </Typography>
          {attemptsLoading ? (
            <Typography color="#6E7D96">Loading attempt history...</Typography>
          ) : attemptRows.length === 0 ? (
            <Typography color="#6E7D96">No redeploy attempts found.</Typography>
          ) : (
            <Table size="small" sx={{ mb: 2 }}>
              <TableHead>
                <TableRow sx={{ background: "#F7FAFF" }}>
                  <TableCell sx={{ fontWeight: 700 }}>Attempt</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Deployment</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Parent</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Created</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {attemptRows.map((row) => (
                  <TableRow
                    key={row.id}
                    hover
                    sx={{ cursor: "pointer" }}
                    onClick={() =>
                      navigate(`/ota/deployments/${row.id}`)
                    }
                  >
                    <TableCell>#{row.attempt_number || "-"}</TableCell>
                    <TableCell
                      sx={{ fontFamily: "monospace", fontSize: 12, color: "#1E4FBA" }}
                    >
                      {row.id}
                    </TableCell>
                    <TableCell sx={{ fontFamily: "monospace", fontSize: 12 }}>
                      {row.parent_deployment_id || "—"}
                    </TableCell>
                    <TableCell>{row.status || "—"}</TableCell>
                    <TableCell>
                      {row.created_at
                        ? formatDateTime(row.created_at)
                        : "—"}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}

          <Typography fontWeight={800} fontSize={15} mb={1}>
            Firmware History {selectedDeviceId ? "(selected device)" : ""}
          </Typography>
          {historyLoading ? (
            <Typography color="#6E7D96">Loading history...</Typography>
          ) : historyRows.length === 0 ? (
            <Typography color="#6E7D96">
              No firmware history available.
            </Typography>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow sx={{ background: "#F7FAFF" }}>
                  <TableCell sx={{ fontWeight: 700 }}>When</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Firmware</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>From</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>To</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {historyRows.slice(0, 12).map((h) => (
                  <TableRow key={h.status_id}>
                    <TableCell>
                      {h.created_at
                        ? formatDateTime(h.created_at)
                        : "—"}
                    </TableCell>
                    <TableCell>
                      {h.firmware_name || "Firmware"} · v
                      {h.firmware_version || "—"}
                    </TableCell>
                    <TableCell>{h.from_version || "—"}</TableCell>
                    <TableCell>{h.to_version || "—"}</TableCell>
                    <TableCell>{h.status || "—"}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </Box>
      </Paper>

      <RollbackModal
        open={openRollback}
        onClose={() => setOpenRollback(false)}
        deploymentId={id}
        rollbackCandidate={detail?.rollback_candidate}
        headers={headers}
        onSuccess={(payload) => {
          const newDeploymentId = payload?.deployment?.id;
          if (newDeploymentId) {
            navigate(`/ota/deployments/${newDeploymentId}`);
            return;
          }
          fetchAll(true);
        }}
      />

      <Dialog
        open={redeployWarningOpen}
        onClose={() => setRedeployWarningOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Pending deployment exists</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Another deployment attempt for this same device and firmware is
            still pending or in progress. Redeploying now will create another
            attempt in the chain. If you want to retry from an older failed
            deployment, you can open that failed attempt and redeploy from
            there instead.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setRedeployWarningOpen(false)}
            sx={{ textTransform: "none" }}
          >
            Cancel
          </Button>
          <Button
            onClick={async () => {
              setRedeployWarningOpen(false);
              await onRedeploy();
            }}
            variant="contained"
            sx={{ textTransform: "none" }}
          >
            Redeploy anyway
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
